import type { AutomationSegment } from '@tms/shared';
import * as RadixSelect from '@radix-ui/react-select';
import { useId, type ReactNode } from 'react';
import { Badge, SelectMenu, SelectMenuItem, SelectMenuLabel } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { MessageKey } from '../../../i18n';
import { cn } from '../../../lib/cn';
import type { PoolAccountSummary, ReadinessMatrix } from '../testrun-types';
import { useVerdictText } from './format';
import {
  groupVerdict,
  unfit,
  type ClassInfo,
  type GroupVerdict,
  type Verdict,
  type VerdictKind,
} from './preflight';

/** A small label over a pre-flight field. */
export const SmallLabel = ({ htmlFor, children }: { htmlFor: string; children: ReactNode }) => (
  <label htmlFor={htmlFor} className="text-[11px] text-ink-subtle">
    {children}
  </label>
);

const MARK: Record<VerdictKind, string> = { ok: '✓', unknown: '?', missing: '✗', broken: '✗' };
const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/**
 * «Pool account» for a group or a class: the choice puts its phone / IIN / BIN
 * into every class of the group. Accounts that fit every class by `requires`
 * come first, then unchecked ones, then those that do not fit, with the reason.
 */
export const PoolPicker = ({
  label,
  classes,
  segment,
  pool,
  readiness,
  current,
  mixed = false,
  onPick,
}: {
  label: string;
  classes: ClassInfo[];
  segment: AutomationSegment | null;
  pool: PoolAccountSummary[];
  readiness: ReadinessMatrix | null | undefined;
  current: PoolAccountSummary | undefined;
  /** the group's classes have different accounts — there is no common «current» */
  mixed?: boolean;
  onPick: (account: PoolAccountSummary) => void;
}) => {
  const { t } = useT();
  const { why, groupText } = useVerdictText();
  const id = useId();
  // pool types are TestRun's own values
  const type = segment === 'IP' ? 'ИП' : segment === 'TOO' ? 'ТОО' : null;
  const rows = pool
    .filter((a) => !type || a.type === type)
    .map((a) => ({ a, v: groupVerdict(readiness, classes, a) }));
  const name = (a: PoolAccountSummary) => a.companyName ?? a.label;
  const byKind = (kind: VerdictKind | null) =>
    rows
      .filter((r) => (r.v?.kind ?? null) === kind)
      .sort((x, y) => (y.v?.fit ?? 0) - (x.v?.fit ?? 0) || name(x.a).localeCompare(name(y.a)));
  const groups: Array<[MessageKey, typeof rows]> = [
    ['automation.runs.picker.fits', byKind('ok')],
    ['automation.runs.picker.unchecked', byKind('unknown')],
    ['automation.runs.picker.unfit', [...byKind('missing'), ...byKind('broken')]],
    ['automation.runs.picker.noRequirements', byKind(null)],
  ];
  const optionText = (a: PoolAccountSummary, v: GroupVerdict | null) =>
    `${v ? `${MARK[v.kind]} ` : ''}${clip(name(a), 40)}${a.companyName && a.label ? ` — ${clip(a.label, 40)}` : ''}${
      v && v.kind !== 'ok' ? `  (${clip(why(v), 60)})` : ''
    }`;
  const cur = current ? groupVerdict(readiness, classes, current) : null;

  return (
    <div className="flex flex-col gap-1">
      <SmallLabel htmlFor={id}>{label}</SmallLabel>
      <SelectMenu
        id={id}
        value={current?.id ?? ''}
        onValueChange={(accountId) => {
          const account = pool.find((a) => a.id === accountId);
          if (account) onPick(account);
        }}
        placeholder={
          mixed ? t('automation.runs.picker.mixed') : t('automation.runs.picker.notInPool')
        }
        className="h-8 text-[13px]"
      >
        {groups
          .filter(([, list]) => list.length)
          .map(([key, list]) => (
            <RadixSelect.Group key={key}>
              <SelectMenuLabel>{t(key, { count: list.length })}</SelectMenuLabel>
              {list.map(({ a, v }) => (
                <SelectMenuItem key={a.id} value={a.id}>
                  {optionText(a, v)}
                </SelectMenuItem>
              ))}
            </RadixSelect.Group>
          ))}
      </SelectMenu>
      {cur ? (
        <p
          className={cn(
            'text-[12px]',
            cur.kind === 'ok'
              ? 'text-pass'
              : cur.kind === 'missing'
                ? 'text-fail'
                : 'text-ink-subtle',
          )}
        >
          {cur.kind === 'ok'
            ? classes.length > 1
              ? t('automation.runs.picker.fitsAll')
              : t('automation.runs.picker.fitsOne')
            : cur.kind === 'missing'
              ? t('automation.runs.picker.doesNotFit', { why: why(cur) })
              : `? ${groupText(cur)}`}
        </p>
      ) : null}
    </div>
  );
};

/** A class's fit on its current pool account: nothing when it fits or has no requirements. */
export const FitBadge = ({ verdict }: { verdict: Verdict | null }) => {
  const { t } = useT();
  const { text } = useVerdictText();
  if (!verdict || verdict.kind === 'ok') return null;
  const bad = unfit(verdict);
  return (
    <Badge className={bad ? 'bg-fail-soft text-fail' : undefined} title={text(verdict)}>
      {bad ? text(verdict) : t('automation.runs.verdict.badgeUnchecked')}
    </Badge>
  );
};
